import { LANGUAGE_TAGS } from "@growthwise/core";
import { useLocation } from "react-router-dom";
import {
  LANGUAGE_LABELS,
  HTML_LANG,
  localizedPath,
  splitLocalePath,
} from "../i18n";
import { useLanguage } from "../i18n/LanguageProvider";

/**
 * Language control.
 *
 * Rendered as real anchors so each translation is a crawlable, copyable,
 * open-in-new-tab-able URL -- and so the control still works if JavaScript
 * has not loaded. The click handler intercepts for a client-side
 * navigation, which is what preserves the current page and any form state
 * held in the React tree.
 */
export const LanguageSwitcher = ({
  compact = false,
}: {
  compact?: boolean;
}) => {
  const { language, switchLanguage, t } = useLanguage();
  const location = useLocation();
  const { rest } = splitLocalePath(location.pathname);

  return (
    <div className="gw-lang" role="group" aria-label={t("nav.language")}>
      {LANGUAGE_TAGS.map((tag) => {
        const href = `${localizedPath(tag, rest)}${location.search}${location.hash}`;
        const active = tag === language;
        return (
          <a
            key={tag}
            href={href}
            hrefLang={HTML_LANG[tag]}
            lang={HTML_LANG[tag]}
            className="gw-lang__option"
            aria-current={active ? "true" : undefined}
            onClick={(event) => {
              // Let the browser handle modified clicks (new tab, download,
              // middle click) rather than hijacking them.
              if (
                event.defaultPrevented ||
                event.metaKey ||
                event.ctrlKey ||
                event.shiftKey ||
                event.altKey ||
                event.button !== 0
              ) {
                return;
              }
              event.preventDefault();
              switchLanguage(tag);
            }}
          >
            {compact ? tag.toUpperCase().slice(0, 2) : LANGUAGE_LABELS[tag]}
          </a>
        );
      })}
    </div>
  );
};
